import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const videos = [
  {
    id: 'demo1',
    label: 'Demo ALPHA-29',
    badge: 'Demo Real · ALPHA-29',
    src: 'https://storage.readdy-site.link/project_files/757146f5-efbb-4393-a727-072a282aedee/3baaac84-8561-4a82-94bb-4471e94f04d7_VID-20260726-WA0096.mp4',
    credit: 'ALPHA-29 — Poly Alphas Chemicals C.A.',
  },
];

export default function VideoDemo() {
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const switchTab = (idx: number) => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setPlaying(false);
    setMuted(true);
    setActiveTab(idx);
  };

  const handlePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.muted = false;
      setMuted(false);
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const handleVideoEnd = () => {
    setPlaying(false);
    if (videoRef.current) videoRef.current.currentTime = 0;
  };

  const current = videos[activeTab];

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-background-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`text-center mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-accent-400 text-sm font-600 tracking-widest uppercase mb-3 block">
            Producto en Acción
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-white leading-tight mb-4">
            ALPHA-29 <span className="gradient-text">Demulsificante</span>
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-foreground-400 text-base max-w-2xl mx-auto">
            Observa en tiempo real cómo nuestro Demulsificante ALPHA-29 separa eficazmente las emulsiones
            de petróleo y agua, demostrando su alto rendimiento en condiciones reales de campo.
          </p>
        </div>



        {/* Video + Info layout */}
        <div className={`grid lg:grid-cols-5 gap-8 items-center transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>

          {/* Video player — 3/5 columns */}
          <div className="lg:col-span-3 relative rounded-2xl overflow-hidden bg-background-950 border border-background-800">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <video
                key={current.src}
                ref={videoRef}
                src={current.src}
                className="absolute inset-0 w-full h-full object-cover"
                muted
                playsInline
                controlsList="nodownload"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onEnded={handleVideoEnd}
              />

              {!playing && (
                <div className="absolute inset-0 bg-background-950/60 flex items-center justify-center">
                  <div
                    onClick={handlePlay}
                    className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-400 transition-all duration-300 cursor-pointer hover:scale-110"
                    style={{ boxShadow: '0 0 40px rgba(var(--primary-500) / 0.5)' }}
                  >
                    <i className="ri-play-fill text-white text-4xl ml-1"></i>
                  </div>
                </div>
              )}

              {playing && (
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center gap-3 bg-gradient-to-t from-background-950/80 to-transparent">
                  <button
                    onClick={handlePlay}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <i className="ri-pause-fill text-white text-base"></i>
                  </button>
                  <button
                    onClick={handleToggleMute}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    <i className={`${muted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-white text-base`}></i>
                  </button>
                  <span className="text-white/60 text-xs ml-auto">{current.credit}</span>
                </div>
              )}

              <div className="absolute top-4 left-4">
                <span className="px-3 py-1.5 rounded-full bg-primary-500 text-white text-xs font-600">
                  {current.badge}
                </span>
              </div>
            </div>
          </div>

          {/* Info panel — 2/5 columns */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="p-5 rounded-xl bg-background-800 border border-background-700">
              <h4 className="font-heading font-700 text-white text-lg mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/20">
                  <i className="ri-flask-line text-accent-400 text-base"></i>
                </div>
                Resultados Demostrados
              </h4>
              <ul className="space-y-3">
                {[
                  { icon: 'ri-drop-line', text: 'Separación visible crudo–agua en minutos' },
                  { icon: 'ri-shield-check-line', text: 'Clarificación total del petróleo crudo' },
                  { icon: 'ri-bar-chart-line', text: 'Mayor eficiencia en refinería' },
                  { icon: 'ri-recycle-line', text: 'Reducción de emulsiones estables' },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-500/15 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-primary-400 text-sm`}></i>
                    </div>
                    <span className="text-foreground-300 text-sm leading-snug">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 rounded-xl bg-background-800 border border-background-700">
              <h4 className="font-heading font-700 text-white text-base mb-3 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/20">
                  <i className="ri-settings-3-line text-primary-400 text-base"></i>
                </div>
                Aplicaciones
              </h4>
              <div className="flex flex-wrap gap-2">
                {['Producción de crudo', 'Refinerías', 'Tanques de almacenamiento', 'Tratamiento en pozo', 'Transporte'].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-accent-100/10 text-accent-300 border border-accent-500/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={() => navigate('/contacto')}
              className="w-full py-3.5 rounded-lg bg-primary-500 hover:bg-primary-400 text-white font-600 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-file-list-3-line"></i>
              Solicitar Cotización ALPHA-29
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}